/* eslint-disable no-unused-vars -- parameter names document vendor adapter contracts */
import { browser } from '$app/environment';
import { hasAnalyticsConsent } from './consent';
import { getPublicEnv } from '$lib/env/publicRuntime';
import { createMetaPixel } from '$lib/utils/meta-pixel';
import { createTikTokPixel } from './tiktok-pixel';
import { createRedditPixel } from './reddit-pixel';

export interface PurchasePayload {
  value: number;
  currency: 'USD';
  sessionId: string;
  contentId: string;
  contentName?: string;
  quantity: number;
}

export interface AdVendor {
  name: string;
  init(): Promise<boolean>;
  pageview(path: string): boolean;
  event(event: string, properties?: Record<string, unknown>, options?: Record<string, unknown>): boolean;
  consent?(allowed: boolean, initial: boolean): void;
}

interface ManagerOptions {
  hasConsent: () => boolean;
  vendors: AdVendor[];
}

const PURCHASE_MARKER_PREFIX = 'quilty_ad_purchase_fired:';
const PURCHASE_MARKER_CAP = 50;

export function createAdTrackingManager({ hasConsent, vendors }: ManagerOptions) {
  const initPromises = new Map<string, Promise<boolean>>();
  const consentNotifications = new Map<string, 'hold' | 'granted' | 'denied'>();
  const pageviews = new Map<string, Set<number>>();
  let currentPath: string | null = null;
  let currentEligible = false;
  let revision = 0;
  let consentWasAllowed = hasConsent();

  function notifyConsent(vendor: AdVendor, allowed: boolean, initial = false): void {
    const state = allowed ? 'granted' : initial ? 'hold' : 'denied';
    if (!vendor.consent || consentNotifications.get(vendor.name) === state) return;
    vendor.consent(allowed, initial);
    consentNotifications.set(vendor.name, state);
  }

  if (!consentWasAllowed) {
    for (const vendor of vendors) notifyConsent(vendor, false, true);
  }

  async function ensureReady(vendor: AdVendor): Promise<boolean> {
    let pending = initPromises.get(vendor.name);
    if (!pending) {
      notifyConsent(vendor, true);
      pending = Promise.resolve()
        .then(() => vendor.init())
        .catch(() => false);
      initPromises.set(vendor.name, pending);
      void pending.then((ready) => {
        if (!ready && initPromises.get(vendor.name) === pending) {
          initPromises.delete(vendor.name);
        }
      });
    }
    return pending;
  }

  async function dispatchPageview(expectedRevision: number): Promise<void> {
    if (!currentPath || !currentEligible || !hasConsent()) return;
    const path = currentPath;
    await Promise.allSettled(
      vendors.map(async (vendor) => {
        if (!(await ensureReady(vendor))) return;
        if (
          revision !== expectedRevision ||
          currentPath !== path ||
          !currentEligible ||
          !hasConsent()
        ) {
          return;
        }
        const sent = pageviews.get(vendor.name) ?? new Set<number>();
        if (sent.has(expectedRevision)) return;
        if (vendor.pageview(path)) {
          sent.add(expectedRevision);
          pageviews.set(vendor.name, sent);
        }
      }),
    );
  }

  async function updatePage(path: string, eligible: boolean): Promise<void> {
    const changed = path !== currentPath || eligible !== currentEligible;
    currentPath = path;
    currentEligible = eligible;
    const allowed = hasConsent();
    const newlyAllowed = allowed && !consentWasAllowed;
    if (allowed !== consentWasAllowed) {
      for (const vendor of vendors) notifyConsent(vendor, allowed);
      consentWasAllowed = allowed;
    }
    if ((!changed && !newlyAllowed) || !eligible || !allowed) return;
    revision += 1;
    await dispatchPageview(revision);
  }

  async function consentChanged(): Promise<void> {
    const allowed = hasConsent();
    const changed = allowed !== consentWasAllowed;
    for (const vendor of vendors) notifyConsent(vendor, allowed);
    if (changed) {
      consentWasAllowed = allowed;
    }
    if (!allowed) {
      return;
    }
    if (changed && currentPath && currentEligible) {
      revision += 1;
      await dispatchPageview(revision);
    }
  }

  async function dispatchEvent(
    mappings: Record<string, string>,
    properties: Record<string, unknown> = {},
    options: Record<string, unknown> = {},
  ): Promise<Record<string, boolean>> {
    const results: Record<string, boolean> = {};
    if (!hasConsent()) return results;
    await Promise.allSettled(
      vendors.map(async (vendor) => {
        if (!(await ensureReady(vendor)) || !hasConsent()) {
          results[vendor.name] = false;
          return;
        }
        const event = mappings[vendor.name];
        results[vendor.name] = event
          ? vendor.event(event, properties, options)
          : false;
      }),
    );
    return results;
  }

  async function trackPurchase(payload: PurchasePayload): Promise<Record<string, boolean>> {
    const results: Record<string, boolean> = {};
    if (!hasConsent()) return results;
    await Promise.allSettled(
      vendors.map(async (vendor) => {
        if (!['meta', 'tiktok', 'reddit'].includes(vendor.name)) return;
        if (hasPurchaseMarker(vendor.name, payload.sessionId)) {
          results[vendor.name] = false;
          return;
        }
        if (!(await ensureReady(vendor)) || !hasConsent()) {
          results[vendor.name] = false;
          return;
        }
        const properties = purchaseProperties(vendor.name, payload);
        const options = vendor.name === 'tiktok'
          ? { event_id: payload.sessionId }
          : {};
        if (vendor.event('Purchase', properties, options)) {
          markPurchase(vendor.name, payload.sessionId);
          results[vendor.name] = true;
        } else {
          results[vendor.name] = false;
        }
      }),
    );
    return results;
  }

  return {
    updatePage,
    consentChanged,
    trackLead: (properties?: Record<string, unknown>) =>
      dispatchEvent(
        { meta: 'Lead', tiktok: 'SubmitForm', reddit: 'Lead' },
        properties,
      ),
    trackRegistration: (properties?: Record<string, unknown>) =>
      dispatchEvent(
        { meta: 'CompleteRegistration', tiktok: 'CompleteRegistration', reddit: 'SignUp' },
        properties,
      ),
    trackPurchase,
  };
}

function purchaseProperties(vendor: string, payload: PurchasePayload): Record<string, unknown> {
  const common = {
    value: Number(payload.value),
    currency: payload.currency,
  };
  const contentName = payload.contentName;
  if (vendor === 'reddit') {
    return {
      ...common,
      conversionId: payload.sessionId,
      itemCount: payload.quantity,
      products: contentName
        ? [{ id: payload.contentId, name: contentName }]
        : [{ id: payload.contentId }],
    };
  }
  if (vendor === 'tiktok') {
    const unitPrice = payload.quantity > 0 ? payload.value / payload.quantity : payload.value;
    return {
      ...common,
      content_type: 'product',
      content_ids: [payload.contentId],
      ...(contentName ? { content_name: contentName } : {}),
      contents: [{
        content_id: payload.contentId,
        ...(contentName ? { content_name: contentName } : {}),
        quantity: payload.quantity,
        price: unitPrice,
      }],
      quantity: payload.quantity,
    };
  }
  return {
    ...common,
    content_type: 'product',
    content_ids: [payload.contentId],
    ...(contentName ? { content_name: contentName } : {}),
    contents: [{ id: payload.contentId, quantity: payload.quantity }],
  };
}

function readPurchaseMarkers(vendor: string): string[] {
  if (!browser) return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(`${PURCHASE_MARKER_PREFIX}${vendor}`) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((value) => typeof value === 'string') : [];
  } catch {
    return [];
  }
}

function hasPurchaseMarker(vendor: string, sessionId: string): boolean {
  return readPurchaseMarkers(vendor).includes(sessionId);
}

function markPurchase(vendor: string, sessionId: string): void {
  if (!browser) return;
  try {
    const values = readPurchaseMarkers(vendor);
    if (!values.includes(sessionId)) values.push(sessionId);
    localStorage.setItem(
      `${PURCHASE_MARKER_PREFIX}${vendor}`,
      JSON.stringify(values.slice(-PURCHASE_MARKER_CAP)),
    );
  } catch {
    // Storage denial must not break checkout completion.
  }
}

function createGoogleVendor(measurementId: string): AdVendor {
  return {
    name: 'google',
    async init() {
      if (!browser || !measurementId || typeof window.gtag !== 'function') return false;
      window.gtag('config', measurementId, { send_page_view: false });
      return true;
    },
    pageview(path) {
      if (typeof window.gtag !== 'function') return false;
      window.gtag('event', 'page_view', {
        page_path: path,
        page_location: window.location.href,
      });
      return true;
    },
    event: () => false,
  };
}

const meta = createMetaPixel(getPublicEnv('PUBLIC_META_PIXEL_ID'));
const tiktok = createTikTokPixel(getPublicEnv('PUBLIC_TIKTOK_PIXEL_ID'));
const reddit = createRedditPixel(getPublicEnv('PUBLIC_REDDIT_PIXEL_ID'));

export const globalAdTracking = createAdTrackingManager({
  hasConsent: hasAnalyticsConsent,
  vendors: [
    createGoogleVendor(getPublicEnv('PUBLIC_GA4_MEASUREMENT_ID')),
    {
      name: 'meta',
      init: meta.init,
      pageview: () => meta.pageView(),
      event: (event, properties) => meta.track(event, properties),
    },
    {
      name: 'tiktok',
      init: tiktok.init,
      pageview: () => tiktok.page(),
      event: (event, properties, options) => tiktok.track(event, properties, options),
      consent: (allowed, initial) => {
        if (allowed) tiktok.grantConsent();
        else if (initial) tiktok.holdConsent();
        else tiktok.revokeConsent();
      },
    },
    {
      name: 'reddit',
      init: reddit.init,
      pageview: () => reddit.pageVisit(),
      event: (event, properties) => reddit.track(event, properties),
    },
  ],
});

export function trackAdLead(properties?: Record<string, unknown>): Promise<Record<string, boolean>> {
  return globalAdTracking.trackLead(properties);
}

export function trackAdRegistration(
  properties?: Record<string, unknown>,
): Promise<Record<string, boolean>> {
  return globalAdTracking.trackRegistration(properties);
}

export function trackAdPurchase(payload: PurchasePayload): Promise<Record<string, boolean>> {
  return globalAdTracking.trackPurchase(payload);
}
